import React from "react";

import {
  HttpError,
  useApiUrl,
  useCustom,
  useDataProvider,
  useNotification,
} from "@refinedev/core";
import {
  SortingState,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  ColumnDef,
  PaginationState,
  getCoreRowModel,
  getPaginationRowModel,
} from "@tanstack/react-table";
import dayjs from "dayjs";
import _ from "lodash";
import { CheckIcon, CopyIcon, Loader2, XIcon } from "lucide-react";

import { DeprecatedDataTable } from "@/components/deprecated-table/data-table";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { FormLabel } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useEventRepository } from "@/pages/events/hooks/use-event-repository";

interface IGiveawayUser {
  id: string;
  email: string;
  claimedOffer: any;
  deviceToken?: string;
  createdAt?: string;
}

export function OfferDialog({
  eventId,
  offer,
}: {
  eventId: string;
  offer: any;
}) {
  const { finaliseGiveaway } = useEventRepository();
  const columns = React.useMemo<ColumnDef<IGiveawayUser>[]>(
    () => [
      {
        id: "select",
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && "indeterminate")
            }
            onCheckedChange={(value) =>
              table.toggleAllPageRowsSelected(!!value)
            }
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
        ),
        enableSorting: false,
        enableHiding: false,
        size: 50,
      },
      {
        id: "email",
        accessorKey: "email",
        header: "Email",
        cell: function render({ getValue }: any) {
          return (
            <div className="p-2 flex flex-row space-x-1 items-center">
              <div>{getValue()}</div>
              <Button
                type="button"
                variant={"ghost"}
                size={"sm"}
                onClick={() => navigator.clipboard.writeText(getValue())}
              >
                <CopyIcon size={16} />
              </Button>
            </div>
          );
        },
        minSize: 300,
      },
      {
        id: "invitations",
        accessorFn: (row) => (row?.claimedOffer?.invitations ?? []).length,
        header: "Invitations",
        enableHiding: true,
      },
      {
        id: "promoCode",
        accessorFn: (row) => row?.claimedOffer?.promoCode,
        header: "Promo Code",
        enableHiding: true,
      },
      {
        id: "deviceToken",
        accessorFn: (row) => row?.deviceToken,
        header: "Has Device Token",
        cell: function render({ getValue }: any) {
          return getValue() ? (
            <CheckIcon className="text-green-700" />
          ) : (
            <XIcon className="text-red-700" />
          );
        },
      },
      {
        id: "createdAt",
        accessorFn: (row) => dayjs(row?.createdAt).format("YYYY-MM-DD"),
        header: "Created At",
      },
      {
        id: "amplitude",
        header: "Amplitude",
        accessorFn: (row) =>
          `https://app.amplitude.com/analytics/musicofourdesire/users?searchType=search&search=${row.id}`,
        cell: function render({ getValue }: any) {
          return (
            <div className="p-2">
              <Button
                type="button"
                variant="default"
                onClick={() => window.open(getValue(), "_blank")}
              >
                See in Amplitude
              </Button>
            </div>
          );
        },
      },
      {
        id: "firebase",
        header: "Firebase",
        accessorFn: (row) =>
          `https://console.firebase.google.com/u/0/project/wheretogo-e5f51/firestore/databases/-default-/data/users/${row.id}/offers/${eventId}`,
        cell: function render({ getValue }: any) {
          return (
            <div className="p-2 space-x-1">
              <Button
                type="button"
                variant="default"
                onClick={() => window.open(getValue(), "_blank")}
              >
                <img
                  className="w-6"
                  src="https://www.gstatic.com/mobilesdk/240501_mobilesdk/firebase_28dp.png"
                />
                See in Firebase
              </Button>
            </div>
          );
        },
      },
    ],
    [],
  );

  const apiUrl = useApiUrl();
  const {
    query: { isLoading },

    result: data,
  } = useCustom<IGiveawayUser[], HttpError>({
    url: `${apiUrl}/events/${eventId}/giveaway`,
    method: "get",
  });

  const [notify, setNotify] = React.useState(true);
  const [loading, setLoading] = React.useState(false);
  const [sorting, setSorting] = React.useState<SortingState>([
    {
      id: "invitations",
      desc: true, // sort by name in descending order by default
    },
  ]); // can set initial sorting state here

  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0, //initial page index
    pageSize: 10, //default page size
  });
  const [columnVisibility, setColumnVisibility] = React.useState({});

  const [rowSelection, setRowSelection] = React.useState({});

  const tableProps = useReactTable<IGiveawayUser>({
    columns,
    data: data?.data ?? [],
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    rowCount: data?.data?.length ?? 0,
    onPaginationChange: setPagination, //update the pagination state when internal APIs mutate the pagination state
    onSortingChange: setSorting, //update the sorting state when internal APIs mutate the sorting state
    onRowSelectionChange: setRowSelection,
    onColumnVisibilityChange: setColumnVisibility,
    state: {
      pagination,
      sorting,
      rowSelection,
      columnVisibility,
    },
  });

  // React.useEffect(() => {
  //   setColumnVisibility({
  //     invitations: offer.type === "giveaway",
  //     promoCodes: offer.type === "discount",
  //   });
  // }, [offer]);

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="default" disabled={isLoading}>
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {isLoading ? "Loading..." : "See Offer Participations"}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-full max-h-svh overflow-auto">
        <DialogHeader>
          <DialogTitle>Edit profile</DialogTitle>
          <DialogDescription>
            See giveaway participations and manage winners.
          </DialogDescription>
        </DialogHeader>
        <DeprecatedDataTable tableProps={tableProps} />
        <DialogFooter>
          {offer.type === "giveaway" && (
            <Drawer>
              <DrawerTrigger asChild>
                <Button type="button">Finalise Giveaway</Button>
              </DrawerTrigger>
              <DrawerContent className="w-[400px]">
                <DrawerHeader>
                  <DrawerTitle>Assign tickets</DrawerTitle>
                  {/* <DrawerDescription>
                  This action cannot be undone.
                </DrawerDescription> */}
                </DrawerHeader>
                <div className="p-4 space-y-2">
                  {offer.ticketsToClaim.map((ticket: any, index: number) => {
                    const rowSelectionIdx: number = Number.parseInt(
                      Object.keys(rowSelection)?.[index],
                    );

                    const user: IGiveawayUser | undefined = !_.isNil(
                      rowSelectionIdx,
                    )
                      ? data?.data?.[rowSelectionIdx]
                      : undefined;

                    return (
                      <div key={index} className="flex flex-col space-y-2">
                        <div className="flex flex-row justify-between">
                          <div
                            key={index}
                            className="text-lg font-bold text-foreground"
                          >{`Ticket ${ticket.id}`}</div>
                          <Button
                            type="button"
                            variant="default"
                            onClick={() => window.open(ticket.url, "_blank")}
                          >
                            See ticket
                          </Button>
                        </div>
                        <Input
                          type="text"
                          value={user?.email ?? ""}
                          onChange={(e) => console.log(e.target.value)}
                        />
                      </div>
                    );
                  })}
                  <div className="flex flex-row justify-between">
                    <Label>Notify users</Label>
                    <Switch checked={notify} onCheckedChange={setNotify} />
                  </div>
                </div>
                <DrawerFooter>
                  <Button
                    type="button"
                    disabled={loading}
                    onClick={async () => {
                      const tickets = offer.ticketsToClaim.map(
                        (ticket: any, index: number) => {
                          const rowSelectionIdx: number = Number.parseInt(
                            Object.keys(rowSelection)?.[index],
                          );

                          const user: IGiveawayUser | undefined = !_.isNil(
                            rowSelectionIdx,
                          )
                            ? data?.data?.[rowSelectionIdx]
                            : undefined;

                          return { ...ticket, claimedBy: user?.id };
                        },
                      );
                      setLoading(true);
                      await finaliseGiveaway(eventId, tickets, notify);
                      setLoading(false);
                    }}
                  >
                    Submit
                    {loading && (
                      <Loader2 className="ml-2 h-4 w-4 animate-spin" />
                    )}
                  </Button>
                  <DrawerClose>Cancel</DrawerClose>
                </DrawerFooter>
              </DrawerContent>
            </Drawer>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
